import {
  MutationResult,
  QueryResult,
  useMutation,
  useQuery,
} from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import Stack from "@mui/material/Stack";
import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useState,
} from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import * as z from "zod";

import { useAppSelector } from "../../../app/hooks";
import { tokenOptions } from "../../../backend/graphql";
import Form from "./Form";
import { useDiamondOptions, DiamondOptions } from "../StoneSearch/useDiamondOptions";
import SectionPaper from "../../widgets/SectionPaper";
import PageTitle from "../../widgets/PageTitle";
import {
  GET_PRICING_ESTIMATION,
  GET_PRICING_FACTORS,
  GetPricingEstimationMutationData,
  GetPricingEstimationMutationVariables,
  GetPricingFactorsQueryData,
  GetPricingFactorsQueryVariables,
} from "./graphql";
import Results from "./Results";

const formSchema = z.object({
  gIAColor: z.string().nullable(),
  langermanColor: z.string().nullable(),
  flatColor: z.string().nullable(),
  meleeColor: z.string().nullable(),
  clarity: z.string().nullable(),
  carat: z.number().positive().nullable(),
  factors: z.array(
    z.object({
      id: z.string(),
      sheetType: z.string(),
      label: z.string(),
      factor: z.number().positive(),
    }),
  ),
});

const validFormSchema = formSchema
  .extend({
    clarity: formSchema.shape.clarity.unwrap(),
    carat: formSchema.shape.carat.unwrap(),
  })
  .superRefine((data, ctx) => {
    if (
      data.gIAColor === null &&
      data.langermanColor === null &&
      data.flatColor === null &&
      data.meleeColor === null
    ) {
      ctx.addIssue({
        path: ["gIAColor"],
        code: "custom",
        message: "At least one color system must be selected.",
      });
      ctx.addIssue({
        path: ["langermanColor"],
        code: "custom",
        message: "At least one color system must be selected.",
      });
      ctx.addIssue({
        path: ["flatColor"],
        code: "custom",
        message: "At least one color system must be selected.",
      });
      ctx.addIssue({
        path: ["meleeColor"],
        code: "custom",
        message: "At least one color system must be selected.",
      });
    }
  });

const mutationInput = validFormSchema.transform((v) => {
  const { factors, ...rest } = v;
  return {
    ...rest,
    pricingFactorIds: factors.map((f) => f.id),
  };
});

type FormData = z.infer<typeof formSchema>;
type ValidFormData = z.infer<typeof validFormSchema>;

type DisplayNormalizedPrices = boolean;
type SubmittedFormValues = ValidFormData | null;

type ContextData = {
  displayNormalizedPricesState: [
    DisplayNormalizedPrices,
    Dispatch<SetStateAction<DisplayNormalizedPrices>>,
  ];
  submittedFormValuesState: [
    SubmittedFormValues,
    Dispatch<SetStateAction<SubmittedFormValues>>,
  ];
  form: UseFormReturn<FormData, any, ValidFormData>;
  factorsResult: QueryResult<
    GetPricingFactorsQueryData,
    GetPricingFactorsQueryVariables
  >;
  options: DiamondOptions | undefined;
  mutationResult: MutationResult<GetPricingEstimationMutationData>;
};

const PricingEstimatorContext = createContext<ContextData | null>(null);

export const usePricingEstimatorContext = () => {
  const context = useContext(PricingEstimatorContext);
  if (!context) {
    throw new Error(
      "usePricingEstimatorContext must be used within a PricingEstimatorContext.Provider",
    );
  }
  return context;
};

function PricingEstimator() {
  const token = useAppSelector((state) => state.auth.token);

  const displayNormalizedPricesState = useState<DisplayNormalizedPrices>(false);
  const submittedFormValuesState = useState<SubmittedFormValues>(null);
  const setSubmittedFormValues = submittedFormValuesState[1];

  const options = useDiamondOptions();
  const factorsResult = useQuery<
    GetPricingFactorsQueryData,
    GetPricingFactorsQueryVariables
  >(GET_PRICING_FACTORS, tokenOptions(token));

  const [estimatePrice, mutationResult] = useMutation<
    GetPricingEstimationMutationData,
    GetPricingEstimationMutationVariables
  >(GET_PRICING_ESTIMATION, tokenOptions(token));

  const form = useForm<FormData, any, ValidFormData>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver<FormData, any, ValidFormData>(validFormSchema),
    defaultValues: {
      gIAColor: null,
      langermanColor: null,
      flatColor: null,
      meleeColor: null,
      clarity: null,
      carat: null,
      factors: [],
    },
  });

  const onValidData = (data: ValidFormData) => {
    const input = mutationInput.parse(data);
    setSubmittedFormValues(data);
    estimatePrice({ variables: input });
  };

  const onSubmit = form.handleSubmit(onValidData);

  return (
    <PricingEstimatorContext.Provider
      value={{
        displayNormalizedPricesState,
        submittedFormValuesState,
        form,
        factorsResult,
        options,
        mutationResult,
      }}
    >
      {/* No gap of its own: the title carries its bottom margin and each section its top one, the
          same as every other page. A gap here as well pushed the first section title down. */}
      <Stack direction="column">
        <PageTitle />
        <SectionPaper>
          <Form onSubmit={onSubmit} />
        </SectionPaper>
        <Results />
      </Stack>
    </PricingEstimatorContext.Provider>
  );
}

export default PricingEstimator;
